const FIELDS = ['Project ID', 'Project Name', 'Role', 'Assignee(s)', 'Hours', 'Rate', 'Value'];

const INCLUDED_PLANNER_TYPES = ['Time + Materials', 'Fixed Fee Deliverable', 'Fixed Fee Project'];

const escapeCSVField = (value) => {
  if (value == null) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const formatNumber = (num) => {
  if (isNaN(num)) return '0.00';
  return Number(num).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatHours = (hours) => Number(hours || 0).toLocaleString();

const formatCurrency = (value) => `$ ${formatNumber(value || 0)}`;

const buildRow = (cells) => cells.map(escapeCSVField).join(',');

const getRolesListForProject = (deliverables, resources) => {
  const allTaskResources =
    deliverables?.flatMap((d) => d?.tasks?.flatMap((t) => t?.resources || []) || []) || [];

  const hoursById = allTaskResources.reduce((acc, r) => {
    if (!r?.isEnabled) return acc;
    const id = r?.id;
    if (!id) return acc;
    acc[id] = (acc[id] || 0) + Number(r?.hours || 0);
    return acc;
  }, {});

  const enabledIds = new Set(allTaskResources.filter((r) => r?.isEnabled && r?.id).map((r) => r.id));

  return (resources || [])
    .filter((resource) => enabledIds.has(resource?.id))
    .map((resource) => {
      const hours = hoursById[resource.id] || 0;
      const rate = Number(resource?.rate || 0);
      return {
        role: resource?.role || '',
        assignees: resource?.assignees || [],
        rate,
        hours,
        value: rate * hours,
      };
    });
};

const joinAssignees = (assignees) => (Array.isArray(assignees) ? assignees.join('; ') : '');

const buildTimeAndMaterialsBlock = (project) => {
  const rolesList = getRolesListForProject(project?.deliverables, project?.resources);

  const roleRows = rolesList.map((role, i) => {
    const idCell = i === 0 ? project?.id : '';
    const nameCell = i === 0 ? project?.projectName : '';
    return buildRow([
      idCell,
      nameCell,
      role.role,
      joinAssignees(role.assignees),
      formatHours(role.hours),
      formatCurrency(role.rate),
      formatCurrency(role.value),
    ]);
  });

  const totalHours = rolesList.reduce((s, r) => s + r.hours, 0);
  const totalValue = rolesList.reduce((s, r) => s + r.value, 0);

  if (roleRows.length === 0) {
    roleRows.push(buildRow([project?.id, project?.projectName, '', '', '', '', '']));
  }

  const summaryRow = buildRow([
    '',
    '',
    '',
    'Hours Total',
    formatHours(totalHours),
    'Services Total',
    formatCurrency(totalValue),
  ]);

  return [...roleRows, summaryRow];
};

const buildFixedFeeBlock = (project, servicesTotal) => {
  const populatedResources = (project?.resources || []).filter((r) => r?.role);

  const roleRows = populatedResources.map((resource, i) => {
    const idCell = i === 0 ? project?.id : '';
    const nameCell = i === 0 ? project?.projectName : '';
    return buildRow([
      idCell,
      nameCell,
      resource.role,
      joinAssignees(resource?.assignees),
      '',
      '',
      '',
    ]);
  });

  if (roleRows.length === 0) {
    roleRows.push(buildRow([project?.id, project?.projectName, '', '', '', '', '']));
  }

  const summaryRow = buildRow([
    '',
    '',
    '',
    'Hours Total',
    formatHours(0),
    'Services Total',
    formatCurrency(servicesTotal),
  ]);

  return [...roleRows, summaryRow];
};

const buildProjectBlock = (project) => {
  const plannerType = project?.plannerType;

  if (plannerType === 'Time + Materials') {
    return buildTimeAndMaterialsBlock(project);
  }

  if (plannerType === 'Fixed Fee Deliverable') {
    const total = (project?.deliverables || []).reduce(
      (s, d) => s + Number(d?.budget || 0),
      0,
    );
    return buildFixedFeeBlock(project, total);
  }

  if (plannerType === 'Fixed Fee Project') {
    return buildFixedFeeBlock(project, Number(project?.plannerServiceValue || 0));
  }

  return [];
};

export const generatePlanRolesCSVData = (projects) => {
  if (!projects || projects.length === 0) return '';

  const eligible = projects.filter((p) => INCLUDED_PLANNER_TYPES.includes(p?.plannerType));
  if (eligible.length === 0) return '';

  const blocks = eligible.map(buildProjectBlock).filter((block) => block.length > 0);
  if (blocks.length === 0) return '';

  const separator = '';
  const body = blocks.reduce((acc, block, idx) => {
    if (idx > 0) acc.push(separator);
    acc.push(...block);
    return acc;
  }, []);

  return [FIELDS.join(','), ...body].join('\n');
};
